import type { BankAccountTypes } from 'types/banks.types';
import type {
  Connector,
  StandingOrderFrequency,
} from 'types/transactions.types';

export enum TransferDocTypes {
  tax = 'tax',
  expense = 'expense',
}
export interface MoneyTransferInitialData {
  sourceAccount?: Connector | Connector['bankAccountReference']['name'];
  receiptAccount?: Connector | BankAccountTypes.external;
  amount?: number;
  // External transfer specific fields
  // Document to attach to the transfer (eg for tax or basket payments)
  document?: {
    id: string;
    type: TransferDocTypes;
  };
  reason?: TransferReasons;
  recipient_iban?: string;
  recipient_name?: string;
  communication?: string;
}

export enum TransferStep {
  ACCOUNT_SELECTION = 1,
  TRANSFER_FORM = 2,
  REVIEW = 3,
}

export interface TransferStepProps {
  onNext?: () => void;
  onBack?: () => void;
}

/**
 * Core transfer data structure - single source of truth
 * This replaces PreservedFormData, TransferSchedule, and parts of TransferReviewData
 */
export interface TransferData {
  /** Transfer amount */
  amount: number;
  /** Recipient name (for external transfers) */
  recipientName?: string;
  /** Recipient IBAN (for external transfers) */
  recipientIBAN?: string;
  /** Communication message */
  communication?: string;
  /** Instant transfer flag (for external transfers) */
  instantTransfer?: boolean;
  /** Transfer schedule information */
  schedule: TransferSchedule;
  /** Transfer type indicator */
  isInternal?: boolean;
}

/**
 * Transfer schedule structure
 * Consolidates all schedule-related data
 */
export interface TransferSchedule {
  /** Standing order frequency */
  period: StandingOrderFrequency;
  /** First execution date */
  firstExecutionDate: Date;
  /** Last execution date (for recurring transfers) */
  lastExecutionDate?: Date;
  /** Requested execution time (for one-time scheduled transfers) */
  requestedExecutionAt?: Date;
}

/**
 * Transfer execution context
 * Contains execution-specific data and functions
 */
export interface TransferExecution {
  /** Original form data (for backwards compatibility) */
  originalFormData?: Record<string, any>;
  /** Mutate function for API calls */
  mutateFunction?: (data: any) => void;
  /** Loading state */
  isLoading?: boolean;
  /** Confirmation callback */
  onConfirm?: () => void;
}

/**
 * Complete transfer context for review step
 * Combines transfer data with execution context
 */
export interface TransferReviewData extends TransferData {
  /** Execution context */
  execution?: TransferExecution;
}

/**
 * Form field names structure for external transfer forms
 * Used for backwards compatibility with existing form components
 */
export interface TransferFormFields {
  recipient_name?: string;
  recipient_iban?: string;
  amount?: number;
  communication?: string;
  period?: string | StandingOrderFrequency;
  firstExecutionDate?: Date;
  lastExecutionDate?: Date;
  requestedExecutionAt?: Date;
}
export enum TransferFieldNames {
  amount = 'amount',
  recipient_name = 'recipient_name',
  recipient_iban = 'recipient_iban',
  communication = 'communication',
  communication_type = 'communication_type',
  instant = 'instant',
  firstExecutionDate = 'firstExecutionDate',
  period = 'period',
  lastExecutionDate = 'lastExecutionDate',
}

export enum CommunicationTypes {
  structured = 'structured',
  unstructured = 'unstructured',
}

export enum TransferTypes {
  sepa = 'sepa',
  sepa_transfer = 'sepa_transfer',
  internal = 'internal',
  instant_sepa = 'instant_sepa',
  expense = 'expense',
  basket = 'basket',
  regular_standing_order = 'regular_standing_order',
  instant_standing_order = 'instant_standing_order',
}

export enum TransferReasons {
  transfer = 'transfer',
  expense = 'expense',
  tax = 'tax',
  basket = 'basket',
}
